import { useCallback, useEffect } from 'react';
import { useSetAtom } from 'jotai';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import DownloadIcon from '@mui/icons-material/Download';
import { TemplateForm } from './TemplateForm';
import { useGetStructureDefectsQuery } from '@/__generated__/graphql';
import { structureDefectLegends } from './state';
import { FormTypes } from './constants';
import { containedButtonStyle } from '@/theme';
import { EmptyStatePlaceholder } from '../Analysis/shared/EmptyStatePlaceholder';

type GenericTemplateProps = {
  structureId: string;
  structureName: string;
};

export const DefectsLegendTemplate = ({ structureId, structureName }: GenericTemplateProps) => {
  const setDefectLegends = useSetAtom(structureDefectLegends);

  const {
    data: defectLegendData,
    loading,
    refetch,
  } = useGetStructureDefectsQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    setDefectLegends(defectLegendData?.structure?.defectLegends);
  }, [defectLegendData?.structure, setDefectLegends]);

  const downloadJson = useCallback(() => {
    refetch().then(({ data }) => {
      const structureDefectLegend = data?.structure?.defectLegends;
      const jsonData = JSON.stringify(
         
        structureDefectLegend?.map(({ __typename, ...defectLegend }) => defectLegend),
        undefined,
        2
      );
      const jsonBlob = new Blob([jsonData], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(jsonBlob);
      const partialName = `${structureName}-defect-legend-${Date.now().toString()}.json`;
      const fileName = partialName;
      link.download = fileName;
      document.body.append(link);
      link.click();
      link.remove();
    });
  }, [refetch, structureName]);

  if (loading) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          backgroundColor: 'rgba(255,255,255,0.6)',
          pt: 20,
          pb: 20,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          borderRadius: '8px',
        }}
      >
        <img
          src="/assets/abyss_fabric_mini_logo_black.svg"
          style={{ height: '6.4rem' }}
          alt="abyss-logo"
        />
      </Stack>
    );
  }

  if (defectLegendData?.structure?.defectLegends?.length === 0) {
    return (
      <Box margin="6.4rem 0">
        <EmptyStatePlaceholder type="template" />
      </Box>
    );
  }

  return (
    <Stack>
      <Stack direction="row" gap={2} sx={{ display: 'flex', pt: 4, alignItems: 'center' }}>
        <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
          <Button
            startIcon={<DownloadIcon />}
            variant="contained"
            onClick={() => downloadJson()}
            sx={containedButtonStyle}
          >
            Download JSON
          </Button>
        </Stack>
      </Stack>
      <TemplateForm
        currentTemplateType={FormTypes.DefectLegends.toString()}
        structureId={structureId}
      />
    </Stack>
  );
};
